import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useDropzone } from "react-dropzone";
import { Col, Row } from "react-bootstrap";
import { MdAttachFile, MdCancel, MdSend } from "react-icons/md";
import { FaUserCircle, FaUsersCog } from "react-icons/fa";

import FieldError from "../../shared/FieldError";
import TextMacro from './TextMacroComponent';
import * as API from "../../api";
import * as UTILS from "../../helpers/Utility";
import { Settings } from "../../helpers/Contexts";

const TaskMessage = Yup.object().shape({
  body: Yup.string()
    .max(2500, "Maximum characters is 1000")
    .min(3, "Message body must be 3 characters or more"),
});
const TaskMessages = ({ messages, state, messageHandler, children }) => {
  const settings = React.useContext(Settings);
  const [messagesArr, setMessages] = useState([...messages]);
  const [error, setError] = useState({ id: null, message: null });
  async function deleteMessage(id) {
    const response = await API.deleteTaskMessage({ id: id });
    response.error && setError({ message: response.message, id: id });
    response.success && setMessages(messagesArr.filter((i) => i.id !== id));
  }
  async function hideMessage(id) {
    const response = await API.updateTaskMessage({ status: 'hidden', id: id });
    response.error && setError({ message: response.message, id: id });
    if (response.success) {
      let newArr = messagesArr.slice();
      let arrIndex = newArr.findIndex((i) => i.id == id);
      newArr.splice(arrIndex, 1, response.success.data.task_message);
      setMessages(newArr);
    }
  }

  async function showMessage(id) {
    const response = await API.updateTaskMessage({ status: 'sent', id: id });
    response.error && setError({ message: response.message, id: id });
    if (response.success) {
      let newArr = messagesArr.slice();
      let arrIndex = newArr.findIndex((i) => i.id == id);
      newArr.splice(arrIndex, 1, response.success.data.task_message);
      setMessages(newArr);
    }
  }

  const TaskMessageContainer = ({ m, settings }) => (
    <>
      <td className="message avatar">
        <FaUserCircle
          style={{ marginBottom: "0px", fontSize: "2em" }}
          id={m.support_agent && m.support_agent_id == settings.me.id ? "logged-in" : ""}
          className="agent-icon"
        />
      </td>
      <td className="message">
        {m.attachment.url && (
          <Link
            to={{ pathname: m.attachment.url }}
            target="_blank"
            className="document btn btn-default auto-width"
            rel="noopener noreferrer"
          >
            <MdAttachFile className="attachment-icon-small" />
            {UTILS.FileNameFromUrl(m.attachment.url)}
          </Link>
        )}
        <div className="message-body">
          <p>{" " + m.body + " "}</p>
        </div>
        <div>
          {m.source == "patient" ? m.patient_name : m.support_agent_name}
          {m.twilio_status && (<><b> ({ m.twilio_status }) </b></>)}
          {m.twilio_error_code && (<><b> (Error Code: { m.twilio_error_code }) </b></>)}
          {m.twilio_error_message && (<><b> ({ m.twilio_error_message }) </b></>)}
          {!m.patient_name && m.pcp_group && m.pcp_group}{" "}
          <i>
            <small>
              ({m.task_message_type || "Internal"} - <b> {m.status}{ m.status == "read" && m.read_at != null ? ' at ' + m.read_at : ''} </b>
                {m.source == "patient" &&
                  (<>
                    <b>
                      { m.origin == 'Text Message' ? (<> - From
                        <i title={"Text Message"} className='blue-text fa fa-phone'></i> Text
                        </>) : (
                          <>
                            - From <i title={"Patient App"} className='blue-text fa fa-comment'></i> App
                          </>
                        ) }
                      </b>
                  </>)
                }
              )
            </small>
          </i>
          <span className="ml10">
            <small>
              {new Date(m.created_at).toLocaleString([], {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </small>
          </span>
          {m && m.support_agent && (settings.me.support_agent_type == 'Admin' || m.support_agent.id == settings.me.id) && (
            <>
              {m.status != 'hidden' && (<>
                <a className="ml5 no-hover" onClick={() => hideMessage(m.id)}>
                  <i className={'red-text fa fa-eye-slash'}></i>
                </a>
              </>)}
              {m.status == 'hidden' && (<>
                <a className="ml5 no-hover" onClick={() => showMessage(m.id)}>
                  <i className={'fa fa-eye'}></i>
                </a>
              </>)}
              <a className="ml5 no-hover" onClick={() => deleteMessage(m.id)}>
                <i className={'red-text fa fa-times'}></i>
              </a>
            </>
          )}
        </div>
      </td>
    </>
  );

  const TaskMessageForm = ({ settings }) => (
    <Formik
      initialValues={{ body: "", attachment: "" }}
      validationSchema={TaskMessage}
      onSubmit={(values) => {
        let valuesInvalid = values.task_message_type == 'text_message' && values.body.length > 2400;
        if (valuesInvalid) {
          alert('There is a 2400 character limit for text messages, please write a shorter text.');
          return;
        };
        const formData = new FormData();
        formData.append("task_message[task_id]", state.currentTask.id);
        formData.append("task_message[body]", values.body);
        formData.append("task_message[task_message_type]", values.task_message_type || "internal");
        values.attachment && formData.append("task_message[attachment]", values.attachment);
        return messageHandler(formData);
      }}
      validateOnChange={true}
    >
      {({ values, setFieldValue, errors, touched, setTouched, isSubmitting }) => {
        const onDrop = useCallback((acceptedFiles) => {
          setFieldValue("attachment", acceptedFiles[0]);
        }, []);
        const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
        return (
          <>
            <Form className="pb20 ">
              <div className="flex">
                <textarea
                  name="body"
                  value={values.body}
                  onChange={(e) => {
                    setFieldValue("body", e.target.value);
                  }}
                  onFocus={() => setTouched(true)}
                  id="task-message-textarea"
                  className="message-body-field"
                  placeholder="Type questions or responses for this task here..."
                />
                <br />
                {settings.patient_dashboard_enabled && (
                  <>
                    <select
                      value={values.task_message_type}
                      className="task-message-visibility-selector"
                      onChange={(e) => {
                        if (e.target.value != undefined) {
                          setFieldValue("task_message_type", e.target.value);
                        }
                      }}
                    >
                      <option>
                        -- Select Type --
                      </option>
                      <option key={"internal"} value="internal">
                        Internal
                      </option>
                      { state.currentTask && state.currentTask.patient && state.currentTask.patient.id && (
                        <option key={"external"} value="external">
                          External
                        </option>
                      )}
                      { state.currentTask.text_messaging_enabled == true && (
                        <option key={"text_message"} value="text_message">
                          Text Message
                        </option>
                      ) }
                    </select>
                  </>
                )}
                {!values.attachment && (
                  <div className="dropzone" {...getRootProps()}>
                    <MdAttachFile className="attachment-icon" />
                    <input {...getInputProps()} />
                  </div>
                )}
                {values.attachment && (
                  <div className="attached-file" key={values.attachment.name}>
                    <MdCancel
                      className="cancel-attached"
                      onClick={() => setFieldValue("attachment", "")}
                    />
                    <MdAttachFile className="attachment-icon-small" />
                    {values.attachment.name}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={!values.body || !values.task_message_type || isSubmitting ? true : false}
                  id={
                    (errors.body && touched) || (!values.body && values.attachment)
                      ? "disabled"
                      : null
                  }
                  className="send-task-message"
                >
                  <MdSend className="send-icon" />
                </button>{" "}
              </div>
              {errors.body && <FieldError error={errors.body} />}
              {!values.body && values.attachment && (
                <FieldError
                  error={"Message body is required. Include a message to send with attachment."}
                />
              )}
              <br />
              <TextMacro task={state.currentTask} setTextFunction={setFieldValue} />
            </Form>
            {settings.patient_dashboard_enabled && (
              <>
                <div className="flex mb20">
                  <b className="gray-text">
                    * DO NOT include any PHI in external task messages unless the patient has
                    consented to receive text messages with PHI.
                  </b>
                </div>
              </>
            )}
          </>
        );
      }}
    </Formik>
  );

  const renderMapTaskMessages = (arr) =>
    arr.map((m) => (
      <tr key={m.id}>
        {m.patient_name && <TaskMessageContainer m={m} settings={settings} />}
        {!m.patient_name && m.support_agent_name != "System" && (
          <TaskMessageContainer m={m} settings={settings} />
        )}
        {!m.patient_name && m.support_agent_name == "System" && (
          <>
            <td className="right user">
              <FaUsersCog style={{ marginBottom: "-10px" }} className="agent-icon" />
            </td>
            <td
              className="message
            "
            >
              <div className="message-body">
                <p>{" " + m.body + " "}</p>
              </div>
              <div>
                {new Date(m.created_at).toLocaleString(
                    [],
                    {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    } + " "
                  )}

                  {m.phone_number && <>
                    { "- From " + m.phone_number }
                  </>}
              </div>
            </td>
          </>
        )}
      </tr>
    ));

  return (
    <>
      <div>
        <table className=" task-message-form px2">
          <tbody>
            <tr>
              <td></td>
              <td className="pl4 py2 ">
                <label>Task Messages</label>
              </td>
            </tr>
            <tr>
              <td></td>
              <td className="pl4">
                <TaskMessageForm settings={settings} />
              </td>
            </tr>
          </tbody>
        </table>
        {!!messagesArr.length > 0 && (
          <div className="task-messages">
            <table className="task-messages-table">
              <tbody>{renderMapTaskMessages(messagesArr)}</tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};
export default TaskMessages;
